import { useContext, useState } from 'react';
import { QueryClientContext } from '@tanstack/react-query';
import { CheckCheck, ExternalLink, Inbox } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import type { Notificacao } from '@/modulos/notificacoes/contrato';
import {
  mensagemDoErro,
  useCaixaDeNotificacoes,
  useMarcarComoLida,
  useMarcarTodasComoLidas,
} from '@/modulos/notificacoes/dados';
import { Notificacoes } from '@/nucleo/navegacao/Notificacoes';
import { RelativeTime } from '@/ui/RelativeTime';
import { Button } from '@/ui/base/button';
import { ScrollArea } from '@/ui/base/scroll-area';
import { Separator } from '@/ui/base/separator';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/ui/base/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/ui/base/tabs';

import {
  agruparPorRecencia,
  centralDaArea,
  classePontoPrioridade,
  iconeDoAviso,
} from './aparencia';

/**
 * Gaveta de notificações do cabeçalho — o sino e o painel que ele abre.
 *
 * Reconstruída a partir da referência aprovada, medida na sessão autenticada em
 * navegador real (registro em `docs/rastreabilidade/matriz-paridade-analises.md`).
 * O que foi observado lá, e está reproduzido aqui:
 *
 * | Da referência | Aqui |
 * |---|---|
 * | Sino `aria-haspopup="dialog"` no cabeçalho, à esquerda do menu da conta | igual |
 * | Painel pela direita, largura total até 420 px, sem respiro próprio | igual |
 * | Título "Notificações" e duas abas, "Não lidas" primeiro | igual |
 * | Rótulo de recência em caixa alta acima do bloco | igual |
 * | Item com avatar redondo, ícone do tipo, ponto de prioridade no canto (sempre visível), título, resumo em duas linhas e data relativa | igual |
 * | Não lida: fundo do item, título em negrito e um segundo ponto na lateral direita | igual |
 * | Botão "Marcar todas como lidas" no cabeçalho, só quando há não lidas | igual |
 * | Vazio com ícone de caixa de entrada e "Nenhuma notificação não lida" | igual |
 * | "Ver mais" no fim da lista, a partir de 20 avisos | igual |
 * | Rodapé com "Ver todas as notificações", que leva à tela da lista | igual, na central da área em que a pessoa está |
 * | Clique no item leva à origem e fecha a gaveta | o clique na não lida a marca como lida; a origem ainda não é calculada pela API |
 *
 * **Dado real, da caixa de quem está logado** (`bff/v1/notificacoes`), em todas
 * as áreas — inclusive `/credenciado`, porque a casca monta o sino em todas. A
 * contagem é a da caixa inteira, devolvida pelo servidor, e se atualiza a cada
 * minuto. Marcar como lida grava, e a central da área vê a mudança pela mesma
 * chave de cache (`modulos/notificacoes/dados.ts`).
 */

/** Quantos avisos a referência mostra antes de oferecer "Ver mais". */
const POR_PAGINA = 20;

/** A API devolve até 100 por página; daí em diante, "Ver todas as notificações". */
const MAXIMO_NA_GAVETA = 100;

/** A contagem do sino se atualiza sozinha a cada minuto. */
const ATUALIZAR_A_CADA_MS = 60_000;

type Aba = 'naoLidas' | 'todas';

function avisarFalha(titulo: string) {
  return (erro: unknown) =>
    toast.error(titulo, { description: mensagemDoErro(erro, 'Tente de novo em instantes.') });
}

function ItemDaGaveta({
  item,
  aoMarcar,
}: {
  item: Notificacao;
  aoMarcar: (item: Notificacao) => void;
}) {
  const Icone = iconeDoAviso(item);

  const corpo = (
    <>
      <div className="relative mt-0.5 shrink-0 rounded-full bg-muted p-2">
        <Icone className="h-4 w-4 text-primary" aria-hidden="true" />
        {/* Ponto de prioridade do avatar: sempre visível, lido ou não. */}
        <span
          className={`absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full border border-card ${classePontoPrioridade(item)}`}
          aria-hidden="true"
        />
      </div>
      <div className="min-w-0 flex-1">
        <p className={`text-sm leading-tight ${item.lida ? '' : 'font-semibold'}`}>{item.titulo}</p>
        <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{item.mensagem}</p>
        <RelativeTime date={item.criadaEm} className="mt-1.5 block text-xs text-muted-foreground" />
      </div>
      {/*
        Ponto lateral de "não lida" — separado do ponto de prioridade do
        avatar, e a única marca de leitura anunciada a quem usa leitor de
        tela (o fundo e o negrito são só visuais).
      */}
      {item.lida ? null : (
        <span className="mt-2 h-2.5 w-2.5 shrink-0 rounded-full bg-orange">
          <span className="sr-only">não lida</span>
        </span>
      )}
    </>
  );

  const forma = `flex w-full items-start gap-3 px-5 py-3.5 text-left transition-colors ${
    item.lida ? '' : 'bg-accent/20'
  }`;

  // Lida, e sem destino calculado: não há o que fazer com ela (regra 7 de interface).
  if (item.lida) {
    return <li className={forma}>{corpo}</li>;
  }

  return (
    <li>
      <button
        type="button"
        onClick={() => aoMarcar(item)}
        aria-label={`${item.titulo}, não lida. Marcar como lida`}
        className={`${forma} hover:bg-accent/50 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset`}
      >
        {corpo}
      </button>
    </li>
  );
}

function CorpoDaGaveta({
  itens,
  total,
  carregando,
  comErro,
  vazio,
  aoMarcar,
  aoVerMais,
}: {
  itens: readonly Notificacao[];
  total: number;
  carregando: boolean;
  comErro: boolean;
  vazio: string;
  aoMarcar: (item: Notificacao) => void;
  aoVerMais: () => void;
}) {
  if (comErro) {
    return (
      <div className="flex flex-col items-center px-5 py-16 text-center text-muted-foreground">
        <Inbox className="mb-3 h-10 w-10 opacity-40" aria-hidden="true" />
        <span className="text-sm">
          Não foi possível carregar as notificações agora. Tente novamente em instantes.
        </span>
      </div>
    );
  }

  if (carregando) {
    return (
      <div
        role="status"
        aria-live="polite"
        className="py-16 text-center text-sm text-muted-foreground"
      >
        Carregando notificações.
      </div>
    );
  }

  if (itens.length === 0) {
    return (
      <div className="flex flex-col items-center py-16 text-muted-foreground">
        <Inbox className="mb-3 h-10 w-10 opacity-40" aria-hidden="true" />
        <span className="text-sm">{vazio}</span>
      </div>
    );
  }

  return (
    <div>
      {agruparPorRecencia(itens).map((grupo) => (
        <div key={grupo.rotulo}>
          <div className="px-5 pb-1 pt-3">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {grupo.rotulo}
            </p>
          </div>
          <ul>
            {grupo.itens.map((item) => (
              <ItemDaGaveta key={item.id} item={item} aoMarcar={aoMarcar} />
            ))}
          </ul>
        </div>
      ))}
      {total > itens.length && itens.length < MAXIMO_NA_GAVETA ? (
        <button
          type="button"
          className="w-full py-3 text-center text-xs font-medium text-primary transition-colors hover:bg-accent/50"
          onClick={aoVerMais}
        >
          Ver mais
        </button>
      ) : null}
    </div>
  );
}

function GavetaComCaixa() {
  const navegar = useNavigate();
  const { pathname } = useLocation();
  const [aberta, setAberta] = useState(false);
  const [aba, setAba] = useState<Aba>('naoLidas');
  const [mostrarNaoLidas, setMostrarNaoLidas] = useState(POR_PAGINA);
  const [mostrarTodas, setMostrarTodas] = useState(POR_PAGINA);

  // A leitura das não lidas roda desde a montagem: a contagem do sino tem de
  // existir antes de alguém abrir a gaveta.
  const naoLidas = useCaixaDeNotificacoes(
    { pagina: 1, tamanho: mostrarNaoLidas, somenteNaoLidas: true },
    { atualizarACadaMs: ATUALIZAR_A_CADA_MS },
  );
  const todas = useCaixaDeNotificacoes(
    { pagina: 1, tamanho: mostrarTodas },
    { habilitada: aberta && aba === 'todas' },
  );
  const marcarComoLida = useMarcarComoLida();
  const marcarTodas = useMarcarTodasComoLidas();

  // Sem dado, o sino fica sem contagem — nunca com aviso de erro no cabeçalho
  // de toda tela.
  const contagem = naoLidas.data?.naoLidas ?? 0;

  function marcar(item: Notificacao) {
    marcarComoLida.mutate(item.id, {
      onError: avisarFalha('A notificação não foi marcada como lida'),
    });
  }

  return (
    <Sheet open={aberta} onOpenChange={setAberta}>
      <SheetTrigger asChild>
        <Notificacoes contagem={contagem} aria-haspopup="dialog" />
      </SheetTrigger>
      <SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-[420px]">
        <SheetHeader className="px-5 pb-3 pt-5">
          <div className="flex items-center justify-between gap-2">
            <SheetTitle className="text-base font-semibold text-foreground">
              Notificações
            </SheetTitle>
            {contagem > 0 ? (
              <Button
                variant="ghost"
                size="sm"
                className="h-auto shrink-0 gap-1 px-2 py-1 text-xs text-primary hover:text-primary"
                disabled={marcarTodas.isPending}
                onClick={() =>
                  marcarTodas.mutate(undefined, {
                    onError: avisarFalha('As notificações não foram marcadas como lidas'),
                  })
                }
              >
                <CheckCheck className="h-3.5 w-3.5" aria-hidden="true" />
                Marcar todas como lidas
              </Button>
            ) : null}
          </div>
          <SheetDescription className="sr-only">
            Últimos avisos do sistema. Escolha entre as não lidas e todas.
          </SheetDescription>
        </SheetHeader>

        <Tabs
          value={aba}
          onValueChange={(valor) => setAba(valor as Aba)}
          className="flex min-h-0 flex-1 flex-col"
        >
          <div className="px-5 pb-2">
            <TabsList className="w-full">
              <TabsTrigger value="naoLidas" className="flex-1">
                Não lidas
              </TabsTrigger>
              <TabsTrigger value="todas" className="flex-1">
                Todas
              </TabsTrigger>
            </TabsList>
          </div>
          <Separator />

          <TabsContent value="naoLidas" className="mt-0 min-h-0 flex-1">
            <ScrollArea className="h-full">
              <CorpoDaGaveta
                itens={naoLidas.data?.itens ?? []}
                total={naoLidas.data?.total ?? 0}
                carregando={naoLidas.isLoading}
                comErro={naoLidas.isError}
                vazio="Nenhuma notificação não lida"
                aoMarcar={marcar}
                aoVerMais={() =>
                  setMostrarNaoLidas((quantos) => Math.min(quantos + POR_PAGINA, MAXIMO_NA_GAVETA))
                }
              />
            </ScrollArea>
          </TabsContent>
          <TabsContent value="todas" className="mt-0 min-h-0 flex-1">
            <ScrollArea className="h-full">
              <CorpoDaGaveta
                itens={todas.data?.itens ?? []}
                total={todas.data?.total ?? 0}
                carregando={todas.isLoading}
                comErro={todas.isError}
                vazio="Nenhuma notificação"
                aoMarcar={marcar}
                aoVerMais={() =>
                  setMostrarTodas((quantos) => Math.min(quantos + POR_PAGINA, MAXIMO_NA_GAVETA))
                }
              />
            </ScrollArea>
          </TabsContent>
        </Tabs>

        <Separator />
        <div className="p-3">
          <Button
            variant="outline"
            size="sm"
            className="w-full text-xs"
            onClick={() => {
              setAberta(false);
              navegar(centralDaArea(pathname));
            }}
          >
            <ExternalLink className="mr-1.5 h-3 w-3" aria-hidden="true" />
            Ver todas as notificações
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

/**
 * Fora de um provedor de consultas — só acontece em teste que monta a casca
 * isolada, sem o `QueryClientProvider` do App — o sino aparece sem contagem e
 * sem gaveta, em vez de derrubar a casca inteira.
 */
export function GavetaDeNotificacoes() {
  const cliente = useContext(QueryClientContext);
  if (!cliente) return <Notificacoes />;
  return <GavetaComCaixa />;
}

export default GavetaDeNotificacoes;
